<template>
  <Dialog :open="modelValue" @update:open="$emit('update:modelValue', $event)">
    <DialogContent
      :id="id"
      :class="['flex flex-col max-h-[85vh] overflow-hidden p-0 gap-0', { 'z-[60]': nested }]"
      :overlay-class="nested ? 'z-[60] bg-black/20' : undefined"
      :style="{ maxWidth: maxWidth }"
      :show-close-button="!modal"
      v-bind="description ? {} : { 'aria-describedby': undefined }"
      @interact-outside="onInteractOutside"
    >
       <div v-if="closing || loading" class="absolute top-0 left-0 w-full h-1 z-50">
          <Progress indeterminate class="h-1" />
       </div>

      <DialogHeader v-if="title || description || !modal" class="shrink-0 p-6 m-0">
        <div class="flex items-center justify-between">
           <DialogTitle class="text-xl">
             {{ title }}
           </DialogTitle>
        </div>
        <DialogDescription v-if="description">
          {{ description }}
        </DialogDescription>
      </DialogHeader>

      <Separator v-if="title" class="shrink-0 my-0" />

      <!--
        The primary content is the only flexible row: it grows to fill the
        available height and scrolls vertically once it would exceed the
        dialog's max height (header/footer stay pinned). overflow-x is hidden
        to avoid a horizontal scrollbar; interactive overlays (Select, Popover,
        DropdownMenu, Tooltip) portal to <body> so they are not clipped by it.

        A scroll container clips at its padding edge, so the dialog's inset
        is applied as *padding* on this element (not as a margin): that keeps
        the focus ring of full-width controls (inputs/textareas use
        `ring-[3px]`, some buttons `ring-2 ring-offset-2` = 4px) inside the
        scrollport instead of cutting it off, and gives the content breathing
        room below the header separator and above the footer one.

        `grid-cols-[minmax(0,1fr)]` is what keeps the dialog at its configured
        width. An implicit `auto` grid track is floored at the *min-content*
        width of its widest item, so a single unshrinkable child (a `Select`
        whose label is long — the trigger is `whitespace-nowrap`) would push
        the track wider than the dialog and drag every sibling out with it.
        `minmax(0, …)` removes that floor, so slot content can never widen the
        dialog; it has to wrap, truncate or scroll instead.
      -->
      <div class="hub-dialog-content grid grid-cols-[minmax(0,1fr)] gap-4 min-w-0 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-5">
        <slot />
      </div>

      <Separator class="shrink-0" />

      <DialogFooter
        class="flex w-full shrink-0 bg-secondary p-6 m-0"
        :class="[btnsRight ? 'justify-end gap-2' : 'justify-between']"
      >
         <!-- Secondary Button -->
         <div :class="[btnsRight ? 'order-1' : '']">
            <TooltipProvider v-if="!hideSecondary">
              <Tooltip :disabled="!secondaryTooltip || !disabledSecondary">
                <TooltipTrigger as-child>
                  <!-- Wrapper, because a disabled button fires no pointer events for the tooltip -->
                  <span class="inline-block">
                    <Button
                      :id="id ? `${id}-secondary-button` : undefined"
                      :disabled="closing || disabledSecondary"
                      variant="ghost"
                      @click="secondaryButton"
                    >
                      {{ secondaryText }}
                      <component v-if="secondaryIcon && isLucide(secondaryIcon)" :is="secondaryIcon" class="ml-2 h-4 w-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{{ secondaryTooltip }}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
         </div>

         <!-- Primary Button -->
         <div :class="[btnsRight ? 'order-2' : 'text-right']">
            <Button
              v-if="!hidePrimary"
              :id="id ? `${id}-primary-button` : undefined"
              :disabled="closing || disabled"
              :variant="destructive ? 'destructive' : 'default'"
              data-cy="hub-dialog-primary-btn"
              @click="primaryButton"
            >
              {{ primaryText }}
              <component v-if="primaryIcon && isLucide(primaryIcon)" :is="primaryIcon" class="ml-2 h-4 w-4" />
            </Button>
         </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * The standard dialog in moin.ai Hub.
 *
 * @displayName Hub Dialog
 */
const props = defineProps({
  id: {
    type: String,
  },
  modelValue: {
    type: Boolean,
  },
  title: {
    type: String,
    default: '',
  },
  /**
   * Optional description shown under the title. Also satisfies reka-ui's
   * accessibility requirement for a dialog description; when omitted the
   * DialogContent is marked as intentionally having no description.
   */
  description: {
    type: String,
    default: '',
  },
  /**
   * if modal is true the dialog can only closed by one button.
   */
  modal: {
    type: Boolean,
    default: false,
  },
  /**
   * Set on a dialog that opens on top of another one.
   *
   * Stacked dialogs both render at `z-50` and paint in the order their portals
   * were created — which is template order, not the order they were opened —
   * so without this the second dialog can end up *behind* the first. It also
   * dims the backdrop far less, since the dialog underneath already brings a
   * full-strength overlay of its own.
   */
  nested: {
    type: Boolean,
    default: false,
  },
  secondaryIcon: {
    // Accepts a lucide component (Object/Function) — rendered via `isLucide`.
    // Legacy icon-font string names are tolerated for backwards-compat but are not rendered.
    type: [String, Object, Function] as PropType<any>,
    default: null,
  },
  hideSecondary: {
    type: Boolean,
    default: false,
  },
  // Disables the secondary button (e.g. while something must be finished first)
  disabledSecondary: {
    type: Boolean,
    default: false,
  },
  // Tooltip explaining why the secondary button is disabled
  secondaryTooltip: {
    type: String,
    default: '',
  },
  hidePrimary: {
    type: Boolean,
    default: false,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  primaryIcon: {
    // Accepts a lucide component (Object/Function) — rendered via `isLucide`.
    // Legacy icon-font string names are tolerated for backwards-compat but are not rendered.
    type: [String, Object, Function] as PropType<any>,
    default: null,
  },
  secondaryLabel: {
    type: String,
    default: null,
  },
  primaryLabel: {
    type: String,
    default: null,
  },
  /**
   * @deprecated Not supported anymore
   */
  primaryColor: {
    type: String,
    default: "primary lighten-2",
  },
  /**
   * The primary button confirms something destructive — a delete, a reset, or
   * throwing away unsaved work. Paints it in the danger colour so the dialog
   * does not invite a confirmation the same way a "Save" dialog does.
   */
  destructive: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  asyncDialog: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: String,
    default: '500px',
  },
  // Align the buttons to the right (if maxWidth to wide)
  btnsRight: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // Normally primary would close or start loading (asyncDialog). With this it turns both of that off.
  manualPrimary: {
    type: Boolean,
    default: false,
  },
  manualSecondary: {
    type: Boolean,
    default: false,
  },
  /**
   * @deprecated No longer needed — HubDialog now always caps its height at
   * `85vh` and scrolls the primary content vertically. Kept so existing
   * callers that still pass it don't break; it has no effect.
   */
  antiOverflow: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  onPrimary: [];
  onSecondary: [];
}>();

const { t } = useI18n();

const closing = ref(false);

const secondaryText = computed(() => {
  if (props.secondaryLabel) return props.secondaryLabel;
  return t('common.cancel');
});

const primaryText = computed(() => {
  if (props.primaryLabel) return props.primaryLabel;
  return t('common.ok');
});

function primaryButton() {
  if (props.manualPrimary) return emit('onPrimary');

  if (props.asyncDialog === false) {
    emit('update:modelValue', false);
  } else {
    closing.value = true;
  }

  emit('onPrimary');
}

function secondaryButton() {
  if (props.manualSecondary) return emit('onSecondary');

  if (props.asyncDialog === false) {
    emit('update:modelValue', false);
  } else {
    closing.value = true;
  }
  emit('onSecondary');
}

function onInteractOutside(e: Event) {
  if (props.modal) {
    e.preventDefault();
  }
}

function isLucide(icon: any) {
  // Only render the icon when it is a Lucide component (object/function).
  // Legacy string icon names from not-yet-migrated callers are ignored.
  return typeof icon === 'object' || typeof icon === 'function';
}

watch(
  () => props.modelValue,
  (toOpen) => {
    if (toOpen) {
      closing.value = false;
    }
  },
);
</script>
<style scoped>
/*
  Thin scrollbar for the scrollable primary content. Firefox gets the standard
  `scrollbar-width`/`scrollbar-color` pair; Chrome/Safari the webkit
  pseudo-elements. Colours come from the shadcn theme tokens so it follows
  light/dark mode.
*/
.hub-dialog-content {
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
}

.hub-dialog-content::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.hub-dialog-content::-webkit-scrollbar-track {
  background: transparent;
}

.hub-dialog-content::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 3px;
}

.hub-dialog-content::-webkit-scrollbar-thumb:hover {
  background: var(--muted-foreground);
}

.hub-dialog-right-row {
  justify-content: end;
}

.hub-dialog-right-col {
  flex-grow: 0;
}
</style>
